import { useEffect, useState } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';
import * as Combobox from '@givebutter/primitives/combobox';
import * as Form from 'components/form';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteWidgets } from 'customHooks/useInfiniteWidgets';

const anyWidgetItem = {
    id: 'any',
    label: 'Any widget',
};

const defaultWidgetSelectValue = [anyWidgetItem.id];

interface WidgetSelectFormValues extends FieldValues {
    widgets: string[];
}

const WidgetSelect = () => {
    const [searchValue, setSearchValue] = useState('');
    const { control, setValue, path } = Form.useGivebutterFormContext<WidgetSelectFormValues>();
    const { widgets } = useWatch({ control });

    const debouncedSearch = useDebounce(searchValue, 250);
    const { data, ref } = useInfiniteWidgets({ query: debouncedSearch });
    const infiniteWidgets = data?.pages ?? [];

    useEffect(() => {
        if (widgets?.length === 0) setValue('widgets', defaultWidgetSelectValue);
    }, [widgets, setValue]);

    return (
        <Form.Container>
            <Form.Label name={path('widgets')}>Widget</Form.Label>
            <Form.Combobox
                control={control}
                name="widgets"
                resource="widget"
                rules={{
                    validate: values => (values && values.length > 0) || 'Widget is required',
                }}
            >
                <Combobox.Trigger aria-label="Select a widget">
                    <Combobox.Select placeholder="Select a widget" />
                </Combobox.Trigger>
                <Combobox.Popover>
                    <Combobox.Search value={searchValue} onChangeValue={setSearchValue} />
                    <Combobox.Items>
                        <Combobox.Item
                            key={anyWidgetItem.id}
                            value={anyWidgetItem.id}
                            onSelectItem={({ event }) => {
                                event.preventDefault();
                                setValue('widgets', defaultWidgetSelectValue, {
                                    shouldDirty: true,
                                });
                            }}
                        >
                            <Combobox.CheckboxIndicator />
                            <Combobox.ItemText>
                                <p className="font-medium">Any widget</p>
                            </Combobox.ItemText>
                        </Combobox.Item>

                        {infiniteWidgets?.map(widget => {
                            const widgetId = widget.id.toString();
                            return (
                                <Combobox.Item
                                    key={widgetId}
                                    value={widgetId}
                                    onSelectItem={({ event, selectedItems, selectedItem }) => {
                                        event.preventDefault();
                                        const selectAnyWidget =
                                            selectedItem === anyWidgetItem.id ||
                                            selectedItems.length === 0;

                                        const newValue = selectAnyWidget
                                            ? defaultWidgetSelectValue
                                            : selectedItems.filter(v => v !== anyWidgetItem.id);

                                        setValue('widgets', newValue, {
                                            shouldDirty: true,
                                        });
                                    }}
                                >
                                    <Combobox.CheckboxIndicator />
                                    <Combobox.ItemText>
                                        <p className="font-medium">{widget.name ?? widget.code}</p>
                                    </Combobox.ItemText>
                                </Combobox.Item>
                            );
                        })}
                        <div ref={ref} />
                    </Combobox.Items>
                </Combobox.Popover>
            </Form.Combobox>
            <Form.Error name={path('widgets')} />
        </Form.Container>
    );
};

export { WidgetSelect, anyWidgetItem, defaultWidgetSelectValue };
export type { WidgetSelectFormValues };
